import { inspectieTypeValues, type InspectieStatus, type InspectieType, type RapportStatus } from "../../../../types";

type ScopeStatusLike = Pick<InspectieStatus, "type" | "status">;

export const isInspectieType = (value: unknown): value is InspectieType => {
  return typeof value === "string" && inspectieTypeValues.includes(value as InspectieType);
};

export const getInspectieTypeParam = (value: unknown): InspectieType | null => {
  return isInspectieType(value) ? value : null;
};

export const getPreferredInspectieType = (entries: ScopeStatusLike[] | null | undefined): InspectieType | null => {
  if (!Array.isArray(entries) || entries.length === 0) return null;

  const preferred = entries.find((entry) => entry.status === "voldoet_niet");
  return (preferred || entries[0])?.type || null;
};

export const getInspectieEntry = (
  entries: InspectieStatus[] | null | undefined,
  inspectieType: InspectieType | null | undefined,
): InspectieStatus | null => {
  if (!inspectieType || !Array.isArray(entries)) return null;
  return entries.find((entry) => entry.type === inspectieType) || null;
};

export const isInspectieAfgerond = (entry: Pick<InspectieStatus, "afgerond"> | null | undefined) => {
  return Boolean(entry?.afgerond);
};

/**
 * Inspection types without an external SCIOS certificate; their certificaat is
 * generated on demand by the printer as an 'inspectiecertificaat_<scope>' document.
 */
export const GENERATED_CERTIFICAAT_TYPES: InspectieType[] = ["nen1010", "nen3140"];

type CertificaatStatusLike = Pick<InspectieStatus, "type"> &
  Partial<Pick<InspectieStatus, "certificaat" | "afgemeld" | "certificaat_pad">>;

/**
 * True when the opdrachtgever can view/download a certificaat for this inspectie
 * entry: an uploaded SCIOS certificate (certificaat_pad in the userFiles bucket),
 * or - for NEN 1010/3140 - the generated inspectiecertificaat once the scope is
 * completed (afgemeld or certificaat flag set).
 */
export const isCertificaatBeschikbaar = (entry: CertificaatStatusLike | null | undefined): boolean => {
  if (!entry) return false;
  if (entry.certificaat && entry.certificaat_pad) return true;
  return GENERATED_CERTIFICAAT_TYPES.includes(entry.type) && Boolean(entry.afgemeld || entry.certificaat);
};

export const getRapportDetailsPath = (rapportId: string, inspectieType: InspectieType) => {
  return `/rapport/${rapportId}/${inspectieType}`;
};

export const getRapportHerstelPath = (rapportId: string, inspectieType: InspectieType) => {
  return `/rapport/${rapportId}/${inspectieType}/herstel`;
};

/** Document type for the printable installateur form generated by the printer service */
export const getHerstelFormulierDocumentType = (inspectieType: InspectieType) => `herstelformulier_${inspectieType}`;

export const getScopeStatusLabel = (status: RapportStatus) => {
  switch (status) {
    case "nieuw":
      return "Nieuw";
    case "bezig":
      return "Bezig";
    case "voldoet":
      return "Voldoet";
    case "voldoet_niet":
      return "Voldoet niet";
    case "hersteld":
      return "Hersteld";
    default:
      return status;
  }
};
